import { useState, useEffect, useRef } from 'react';
import { Button, Checkbox, Form, Input } from 'antd';
import PhoneInput from 'react-phone-number-input'
import { useDispatch, useSelector } from 'react-redux'
import { formHasErrors } from '../../../shared/utils/functions/handleFormChanges';
import { validationMsgs } from '../../../shared/utils/constantData';
import { CountrySelectProps } from '../../../shared/utils/interfaces/landingPageFace';
import { selectBookDemoData, setBookDemoData, setCurrentStepperStep } from '../slice/contactusSlice'
import dropdownArrow from '../../../shared/assets/images/ChevronDownLightGray.svg'
import 'react-phone-number-input/style.css'
import { parsePhoneNumberFromString } from 'libphonenumber-js/max';


export const BookADemoForm = () => {
  const [form] = Form.useForm();
  const bookDemoData = useSelector(selectBookDemoData)
  const [isDisabled, setIsDisabled] = useState([
    bookDemoData?.firstName,
    bookDemoData?.lastName,
    bookDemoData?.jobTitle,
    bookDemoData?.email,
    bookDemoData?.companyName,
  ].some((value) => !value || value.trim() === ""))
  const dispatch = useDispatch();
  const [value, setValue] = useState<string | undefined>(bookDemoData?.phone || undefined)

  function CountrySelect({ options: countriesCode, value, onChange, iconComponent: IconComponent }: CountrySelectProps) {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Close dropdown on outside click
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef?.current && !dropdownRef?.current?.contains(event?.target as Node)) {
          setIsOpen(false);
        }
      };

      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
      } else {
        document.removeEventListener('mousedown', handleClickOutside);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [isOpen]);

    return (
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-2 text-gray-400 !p-0 border-none !font-normal shadow-none"
        >
          <span className='text-black'>{value || 'Select'}</span>
          <img
            src={dropdownArrow}
            alt="Dropdown Arrow"
            className="inline-block w-4 h-4 ml-2"
          />
        </button>

        {isOpen && (
          <ul className="absolute z-10 p-0 py-3 mt-1 overflow-auto text-left bg-white border rounded shadow -left-3 w-72 max-h-60">
            {countriesCode?.map((country) => (
              <li
                key={country?.value}
                className={`flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-indigo-50 ${value === country?.value ? 'bg-indigo-50' : ''}`}
                onClick={() => {
                  onChange(country?.value);
                  setIsOpen(false);
                }}
              >
                {IconComponent && (
                  <IconComponent country={country?.value} label={country?.label} />
                )}
                <span>{country?.label}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  const handleFormChange = () => setIsDisabled(formHasErrors(form))

  const onFinish = async (values: any) => {
    dispatch(setBookDemoData({ ...bookDemoData, ...values }))
    dispatch(setCurrentStepperStep(1))
  };

  return (
    <div>
      <Form
        form={form}
        onFinish={onFinish}
        onFieldsChange={handleFormChange}
        scrollToFirstError
        autoComplete="off"
        size='large'
        className='text-left'
        initialValues={{
          firstName: bookDemoData?.firstName,
          lastName: bookDemoData?.lastName,
          jobTitle: bookDemoData?.jobTitle,
          email: bookDemoData?.email,
          phone: bookDemoData?.phone,
          companyName: bookDemoData?.companyName,
          receiveEmails: bookDemoData?.receiveEmails || false
        }}
      >
        <div className='flex flex-col justify-between sm:flex-row sm:gap-5'>
          <Form.Item
            className='w-full'
            name="firstName"
            rules={[{ required: true, message: 'First name is missing' }]}
            hasFeedback
          >
            <Input placeholder='First Name' />
          </Form.Item>

          <Form.Item
            className='w-full'
            name="lastName"
            rules={[{ required: true, message: 'Last name is missing' }]}
            hasFeedback
          >
            <Input placeholder='Last Name' />
          </Form.Item>
        </div>

        <Form.Item
          name="jobTitle"
          rules={[{ required: true, message: 'Job title is missing' }]}
          hasFeedback
        >
          <Input placeholder='Job Title' />
        </Form.Item>

        <Form.Item
          name="email"
          rules={[
            { type: 'email', message: validationMsgs.invalidEmail },
            { required: true, message: 'Email address is missing' },
          ]}
          hasFeedback
        >
          <Input placeholder='Work Email' />
        </Form.Item>

        <Form.Item
          name="phone"
          rules={[
            {
              validator: async (_, value) => {
                if (!value) return Promise.resolve();
                
                if (/^\+\d{1,3}$/.test(value)) {
                  return Promise.resolve();
                }
                const phoneNumberWithoutSpaces = String(value).replace(/\s+/g, '');

                const toParse = phoneNumberWithoutSpaces.startsWith('+') ? phoneNumberWithoutSpaces : `+${phoneNumberWithoutSpaces}`;
                
                try {
                  const phone = parsePhoneNumberFromString(toParse);

                  if (!phone) {
                    return Promise.reject(new Error('Please enter a valid phone number'));
                  }

                  if (!phone.isValid()) {
                    return Promise.reject(new Error('Please enter a valid phone number'));
                  }

                  if(phone.getType() === 'FIXED_LINE'){
                    return Promise.reject(new Error('Please enter a valid phone number'));
                }

                  return Promise.resolve();
                } catch (err) {
                  return Promise.reject(new Error('Please enter a valid phone number'));
                }
              },
            },
          ]}
          hasFeedback
        >
          <div className="relative">
            <PhoneInput
              className='py-[0.375rem] px-3 rounded-md border text-gray-400 border-gray-300 hover:border-indigo-600 focus-within:border-indigo-600'
              defaultCountry="US"
              international
              value={value}
              onChange={(newVal) => {
                setValue(newVal)
                form.setFieldsValue({ phone: newVal })
                form.validateFields(['phone']).catch(() => { })
              }}
              countrySelectComponent={CountrySelect}
              countryCallingCodeEditable={false}
            />

            {!value && (
              <span className="absolute text-sm text-gray-400 transform -translate-y-1/2 pointer-events-none right-3 top-1/2">
                (optional)
              </span>
            )}

          </div>
        </Form.Item>

        <Form.Item
          name="companyName"
          rules={[
            { required: true, message: 'Company name is missing' },
          ]}
          hasFeedback
        >
          <Input placeholder='Company name' />
        </Form.Item>

        <Form.Item
          name="receiveEmails"
          valuePropName="checked"
        >
          <div className='flex items-start gap-2'>
            <Checkbox className='!w-fit' defaultChecked={bookDemoData?.receiveEmails || false}></Checkbox>
            <span className='!text-xs font-normal text-gray-500 text-left'>
              Yes, I’d like to receive helpful resources like tutorials, templates and the latest hiring advice, as well as invitations to peopleCQ events.
              (You can opt out any time)
            </span>
          </div>
        </Form.Item>

        <Form.Item className='mb-0'>
          <Button className='w-full btn-primary' htmlType="submit" disabled={isDisabled} loading={false}>
            Continue
          </Button>
        </Form.Item>
      </Form>
    </div>
  )
}
